import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef, ExcelExportParams, GridApi, GridReadyEvent, SizeColumnsToContentStrategy, SizeColumnsToFitGridStrategy, SizeColumnsToFitProvidedWidthStrategy } from 'ag-grid-community';
import { NgDialogAnimationService } from 'ng-dialog-animation';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, finalize, forkJoin, of, Subject, Subscription, takeUntil } from 'rxjs';
import { HrAlertService, HrAlertStatus, HrHttpService, MrfLocationFilters } from '../hr-http.service';
import { MrfFilterDialogComponent, MrfFilterDialogData } from '../mrf-filter-dialog/mrf-filter-dialog.component';
import {
  MrfDashboardFilterTab,
  MrfDashboardStateService,
  MrfFilterDialogField,
  MrfFilterDialogMode,
} from '../mrf-dashboard-state.service';
import {
  canAccessMrfDashboardTab,
  formatMrfDateForDisplay,
  getAllowedMrfDashboardRowTabStatuses,
  getVisibleMrfDashboardTabs,
  hasMrfDashboardAccess,
  MrfHrmsPermissions,
  MrfViewOption,
  EMPTY_MRF_HRMS_PERMISSIONS,
  normalizeMrfWorkflowStatus,
  resolveMrfViewOptions,
} from '../mrf-view-options';

type MrfFilterTab = MrfDashboardFilterTab;

@Component({
  selector: 'app-mrf-dashboard',
  templateUrl: './mrf-dashboard.component.html',
  styleUrls: ['./mrf-dashboard.component.scss']
})
export class MrfDashboardComponent implements OnInit, OnDestroy {
  activeTab: MrfFilterTab = 'ALL';
  cityFilter = 'City wise';
  readonly cityOptions = ['City wise', 'Hyderabad', 'Bangalore', 'Chennai'];

  /** Summary period: All → existing dashboard API; month 1–12 → dateWise API. */
  readonly monthShortLabels = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ] as const;
  /** Default restored from MrfDashboardStateService on init. */
  selectedSummaryPeriod: 'all' | number = new Date().getMonth() + 1;
  summaryMonthYear = new Date().getFullYear();
  summaryBrowseYear = new Date().getFullYear();
  summaryMonthMenuOpen = false;
  private readonly maxSummaryMonth = new Date().getMonth() + 1;
  private readonly maxSummaryYear = new Date().getFullYear();

  get summaryMonthLabel(): string {
    const month =
      this.selectedSummaryPeriod === 'all'
        ? this.maxSummaryMonth
        : Number(this.selectedSummaryPeriod);
    const label = this.monthShortLabels[month - 1] ?? '';
    return `${label} ${this.summaryMonthYear}`;
  }

  get canGoNextSummaryYear(): boolean {
    return this.summaryBrowseYear < this.maxSummaryYear;
  }

  colDefs: ColDef<any>[] = [
    {
      field: 'requestId',
      headerName: 'MRF Id',
      cellStyle: { 'text-align': 'left' },
      headerStyle: { 'text-align': 'center' },
    },
    {
      field: 'deanName',
      headerName: 'Dean Name',
      headerStyle: { 'text-align': 'center' },
    },
    {
      field: 'campusName',
      headerName: 'Campus Name',
      headerStyle: { 'text-align': 'center' },
    },
    {
      field: 'department',
      headerName: 'Department',
      headerStyle: { 'text-align': 'center' },
    },
    {
      field: 'designation',
      headerName: 'Designation',
      headerStyle: { 'text-align': 'center' },
    },
    {
      field: 'subject',
      headerName: 'Subject',
      headerStyle: { 'text-align': 'center' },
    },
    {
      field: 'date',
      headerName: 'MRF Date',
      cellRenderer: (params: any) =>
        params.value ? String(params.value).replace('T', ' ') : '',
    },
    {
      field: 'mobileNo',
      headerName: 'Mobile No.',
      headerStyle: { 'text-align': 'center' },
    },
    {
      field: 'status',
      headerName: 'Status',
      headerStyle: { 'text-align': 'center' },
      cellStyle: {
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
      },
      minWidth: 150,
      cellRenderer: (params: any) =>
        this.buildStatusCell(params.data?.rawStatus ?? params.value, params.value),
    },
    {
      field: 'view',
      headerName: 'View',
      minWidth: 110,
      maxWidth: 130,
      cellRenderer: () => this.buildViewButtonCell(),
      cellStyle: { overflow: 'visible', 'text-align': 'center' },
      suppressNavigable: true,
      onCellClicked: (params: any) => this.onViewCellClicked(params),
    },
  ];

  viewMenuOpen = false;
  viewMenuTop = 0;
  viewMenuLeft = 0;
  readonly viewMenuMinWidth = 180;
  viewMenuOptions: MrfViewOption[] = [];
  activeViewMrfId = '';

  rowData: any[] = [];
  displayRowData: any[] = [];
  tableData: any[] = [];
  pinnedTopRowData: any[] = [];
  private gridApi: GridApi | null = null;
  readonly defaultExcelExportParams: ExcelExportParams = {
    exportAsExcelTable: true,
  };
  locationFilters: MrfLocationFilters = {};
  filterMode: MrfFilterDialogMode = 'default';
  customizePrimaryFilterBy: MrfFilterDialogField | null = null;

  defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    filter: false,
    menuTabs: [],
    suppressHeaderMenuButton: true,
  };

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: 'fitCellContents',
  };

  mrfData: any[] = [];
  quickFilter = '';

  /** Summary cards and grid row statuses. */
  readonly mrfStatusNames = [
    'TOTAL RECEIVED',
    'REQUESTED',
    'PENDING',
    'APPROVED',
    'REJECTED',
  ] as const;

  /** Row-level statuses only (excludes total). */
  readonly rowStatusNames = ['REQUESTED', 'PENDING', 'APPROVED', 'REJECTED'] as const;

  /** Table row display order by workflow status. */
  private readonly tableRowSortOrder = [
    'REQUESTED',
    'APPROVAL',
    'PENDING_AT_MIS',
    'PENDING_AT_HR',
    'APPROVED',
    'APPROVED_AT_HR',
    'REJECTED',
  ] as const;

  readonly desiredOrder = [...this.mrfStatusNames];

  userRoleDetails: Record<string, unknown> | null = null;
  mrfPermissions: MrfHrmsPermissions = { ...EMPTY_MRF_HRMS_PERMISSIONS };
  rolesLoaded = false;
  /** Allowed filter statuses from track history API (`statuses` array). */
  private trackHistoryAllowedStatuses: string[] = [];
  private trackHistoryStatusFilterActive = false;

  dashboardAlert: { message: string; status: HrAlertStatus } | null = null;
  private dashboardAlertTimer?: ReturnType<typeof setTimeout>;
  private readonly destroy$ = new Subject<void>();
  selectedTrackDuration: '1MONTH' | '2MONTH' | '3MONTH' = '1MONTH';
  private dashboardFetchSub?: Subscription;
  private trackHistorySub?: Subscription;
  private pendingLoadCount = 0;

  get canCreateRequest(): boolean {
    return this.mrfPermissions.requestForm;
  }

  get hasDashboardAccess(): boolean {
    return hasMrfDashboardAccess(this.mrfPermissions);
  }

  get canExportExcel(): boolean {
    if (!this.hasDashboardAccess) {
      return false;
    }
    if (this.gridApi) {
      return this.gridApi.getDisplayedRowCount() > 0;
    }
    return this.displayRowData.length > 0;
  }

  get visibleTabs(): MrfFilterTab[] {
    return getVisibleMrfDashboardTabs(this.mrfPermissions);
  }

  constructor(
    private hrHttp: HrHttpService,
    private hrAlert: HrAlertService,
    private filterDialog: NgDialogAnimationService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private dashboardState: MrfDashboardStateService
  ) {}

  ngOnInit(): void {
    this.bindDashboardState();
    this.showPendingDashboardAlert();
    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.dashboardFetchSub?.unsubscribe();
    this.trackHistorySub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.dashboardAlertTimer) {
      clearTimeout(this.dashboardAlertTimer);
    }
  }

  private beginLoading(): void {
    this.pendingLoadCount += 1;
    if (this.pendingLoadCount === 1) {
      void this.spinner.show();
    }
  }

  private endLoading(): void {
    this.pendingLoadCount = Math.max(0, this.pendingLoadCount - 1);
    if (this.pendingLoadCount === 0) {
      void this.spinner.hide();
    }
  }

  private bindDashboardState(): void {
    this.quickFilter = this.dashboardState.quickFilter;
    this.activeTab = this.dashboardState.activeTab;
    this.locationFilters = this.dashboardState.locationFilters;
    this.filterMode = this.dashboardState.filterMode;
    this.customizePrimaryFilterBy = this.dashboardState.customizePrimaryFilterBy;
    this.applySummaryPeriodState(this.dashboardState.summaryPeriod);

    this.dashboardState.quickFilter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.quickFilter = value;
      });

    this.dashboardState.activeTab$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tab) => {
        this.activeTab = tab;
        this.updateDisplayedRows();
      });

    this.dashboardState.locationFilters$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.locationFilters = filters;
      });

    this.dashboardState.filterMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        this.filterMode = mode;
      });

    this.dashboardState.customizePrimaryFilterBy$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filterBy) => {
        this.customizePrimaryFilterBy = filterBy;
      });

    this.dashboardState.summaryPeriod$
      .pipe(takeUntil(this.destroy$))
      .subscribe((summary) => {
        this.applySummaryPeriodState(summary);
      });
  }

  private applySummaryPeriodState(summary: {
    selectedSummaryPeriod: 'all' | number;
    summaryMonthYear: number;
  }): void {
    this.selectedSummaryPeriod = summary.selectedSummaryPeriod;
    this.summaryMonthYear = summary.summaryMonthYear;
    if (!this.summaryMonthMenuOpen) {
      this.summaryBrowseYear = summary.summaryMonthYear;
    }
  }

  dismissDashboardAlert(): void {
    this.dashboardAlert = null;
    if (this.dashboardAlertTimer) {
      clearTimeout(this.dashboardAlertTimer);
      this.dashboardAlertTimer = undefined;
    }
  }

  getDashboardAlertBackground(status: HrAlertStatus): string {
    if (status === 'Warning') return '#ffeeba';
    if (status === 'Error') return '#f5c6cb';
    return '#c3e6cb';
  }

  private showPendingDashboardAlert(): void {
    const pending = this.hrAlert.consumePendingDashboardAlert();
    if (!pending) return;

    this.dashboardAlert = pending;
    if (this.dashboardAlertTimer) {
      clearTimeout(this.dashboardAlertTimer);
    }
    this.dashboardAlertTimer = setTimeout(() => this.dismissDashboardAlert(), 4000);
  }

  onQuickFilterChanged(event: Event): void {
    this.dashboardState.setQuickFilter((event.target as HTMLInputElement).value);
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  exportToExcel(): void {
    if (!this.canExportExcel || !this.gridApi) {
      this.hrAlert.show('No data available to export.', 'Warning');
      return;
    }

    const exportColumns = this.colDefs
      .map((col) => col.field)
      .filter((field): field is string => !!field && field !== 'view');

    this.gridApi.exportDataAsExcel({
      ...this.defaultExcelExportParams,
      fileName: this.buildExcelFileName(),
      sheetName: 'MRF Track Details',
      columnKeys: exportColumns,
      skipPinnedTop: true,
      processCellCallback: (params) => {
        const colId = params.column.getColId();
        if (colId === 'date' && params.value) {
          return String(params.value).replace('T', ' ');
        }
        if (colId === 'status') {
          return params.node?.data?.status ?? params.value ?? '';
        }
        return params.value ?? '';
      },
    });

    this.hrAlert.show('Excel report generated. Please check your downloads.', 'Success');
  }

  private buildExcelFileName(): string {
    const stamp = new Date().toISOString().slice(0, 10);
    const tab = String(this.activeTab ?? 'ALL').replace(/\s+/g, '_');
    return `MRF_Track_Details_${tab}_${stamp}`;
  }

  buildViewButtonCell(): string {
    const openClass = this.viewMenuOpen ? ' mrf-view-btn--open' : '';
    return `
      <button type="button" class="mrf-view-btn${openClass}">
        View
        <span class="mrf-view-btn__chev" aria-hidden="true"></span>
      </button>
    `;
  }

  onViewCellClicked(params: any): void {
    const event = params?.event as MouseEvent | undefined;
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const row = params?.data ?? {};
    const mrfId = String(row.requestId ?? '').trim();
    const rawStatus = String(row.rawStatus ?? row.status ?? '').trim();
    const options = resolveMrfViewOptions(rawStatus, this.mrfPermissions);
    if (!mrfId || !options.length) return;

    if (options.length === 1) {
      this.closeViewMenu();
      this.router.navigate([options[0].path], { queryParams: { mrfId } });
      return;
    }

    const cellEl =
      (params.eGridCell as HTMLElement | undefined) ??
      (event?.target as HTMLElement | undefined)?.closest('.ag-cell') ??
      null;
    const container = cellEl?.closest('.table_container') as HTMLElement | null;
    if (!cellEl || !container) return;

    if (this.viewMenuOpen && this.activeViewMrfId === mrfId) {
      this.closeViewMenu();
      return;
    }

    const rect = cellEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    this.activeViewMrfId = mrfId;
    this.viewMenuOptions = options;
    this.viewMenuTop = rect.bottom - containerRect.top + 4;
    this.viewMenuLeft = Math.max(8, rect.right - containerRect.left - this.viewMenuMinWidth);
    this.viewMenuOpen = true;
  }

  onViewMenuScroll(): void {
    this.closeViewMenu();
  }

  onViewMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  selectViewOption(option: MrfViewOption, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.activeViewMrfId) return;
    const mrfId = this.activeViewMrfId;
    this.closeViewMenu();
    this.router.navigate([option.path], { queryParams: { mrfId } });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.mrf-view-menu') || target?.closest('.mrf-view-btn')) {
      return;
    }
    this.closeViewMenu();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.closeViewMenu();
  }

  private closeViewMenu(): void {
    if (!this.viewMenuOpen) return;
    this.viewMenuOpen = false;
    this.viewMenuOptions = [];
    this.activeViewMrfId = '';
  }

  createRequest(): void {
    if (!this.canCreateRequest) return;
    this.router.navigate(['/hrDetails/manPowerRequisitionForm']);
  }

  openFilterDialog(): void {
    this.openMrfFilterDialog('default');
  }

  openCustomizeFilterDialog(): void {
    this.openMrfFilterDialog('customize');
  }

  private openMrfFilterDialog(mode: MrfFilterDialogData['mode']): void {
    const isCustomize = mode === 'customize';
    const dialogRef = this.filterDialog.open(MrfFilterDialogComponent, {
      width: isCustomize ? '52vw' : '40vw',
      minWidth: isCustomize ? '580px' : '420px',
      height: '100%',
      position: { right: '0' },
      maxWidth: '100%',
      animation: { to: 'left' },
      autoFocus: false,
      disableClose: true,
      data: { mode },
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.status === 'search') {
        this.dashboardState.setFilterMode(data.filterMode ?? 'default');
        this.dashboardState.setCustomizePrimaryFilterBy(data.primaryFilterBy ?? null);
        this.dashboardState.setLocationFilters(data.filters ?? {});
        this.loadDashboard();
        return;
      }
      if (data?.status === 'reset') {
        this.dashboardState.setFilterMode(data.filterMode ?? 'customize');
        this.dashboardState.setCustomizePrimaryFilterBy(null);
        this.dashboardState.setLocationFilters({});
        this.dashboardState.setFilterDialogState(null);
        this.loadDashboard();
      }
    });
  }

  loadDashboard(): void {
    this.fetchDashboardData({ includeRoles: true });
  }

  private reloadDashboardData(): void {
    this.fetchDashboardData({ includeRoles: false });
  }

  private fetchDashboardData(options: { includeRoles: boolean }): void {
    this.dashboardFetchSub?.unsubscribe();
    this.beginLoading();

    const requests: {
      counts: ReturnType<MrfDashboardComponent['getSummaryCountsRequest']>;
      history: ReturnType<MrfDashboardComponent['getTrackHistoryRequest']>;
      roles?: ReturnType<HrHttpService['getUserRoles']>;
    } = {
      counts: this.getSummaryCountsRequest().pipe(
        catchError((err) => {
          this.handleSummaryCountsError(err);
          return of(null);
        })
      ),
      history: this.getTrackHistoryRequest().pipe(
        catchError((err) => {
          console.error('MRF track history failed', err);
          this.tableData = [];
          this.rowData = [];
          this.updateDisplayedRows();
          return of(null);
        })
      ),
    };

    if (options.includeRoles) {
      requests.roles = this.hrHttp.getUserRoles().pipe(catchError(() => of(null)));
    }

    this.dashboardFetchSub = forkJoin(requests)
      .pipe(finalize(() => this.endLoading()))
      .subscribe(({ counts, history, roles }) => {
        if (options.includeRoles) {
          this.rolesLoaded = true;
          if (roles != null) {
            this.userRoleDetails =
              roles && typeof roles === 'object' ? (roles as Record<string, unknown>) : null;
            this.mrfPermissions = this.hrHttp.setMrfHrmsPermissionsFromResponse(roles);
          } else {
            this.userRoleDetails = null;
            this.mrfPermissions = { ...EMPTY_MRF_HRMS_PERMISSIONS };
          }
          this.syncActiveTabWithPermissions();
        }

        if (counts != null) {
          this.applyCountDetailsResponse(counts);
        }
        if (history != null) {
          this.applyTrackHistoryResponse(history);
        } else {
          this.clearTrackHistoryStatusFilter();
          this.tableData = [];
          this.rowData = [];
          this.updateDisplayedRows();
        }
      });
  }

  onSummaryPeriodSelect(value: 'all' | number): void {
    this.summaryMonthMenuOpen = false;
    if (this.dashboardState.selectedSummaryPeriod === value) {
      return;
    }
    this.dashboardState.setSummaryPeriod(value);
    this.reloadDashboardData();
  }

  toggleSummaryMonthMenu(): void {
    if (!this.summaryMonthMenuOpen) {
      this.summaryBrowseYear = this.summaryMonthYear;
    }
    this.summaryMonthMenuOpen = !this.summaryMonthMenuOpen;
  }

  shiftSummaryMonthYear(delta: number): void {
    const nextYear = this.summaryBrowseYear + delta;
    if (nextYear > this.maxSummaryYear) {
      return;
    }
    this.summaryBrowseYear = nextYear;
  }

  isSummaryMonthDisabled(month: number): boolean {
    return (
      this.summaryBrowseYear > this.maxSummaryYear ||
      (this.summaryBrowseYear === this.maxSummaryYear && month > this.maxSummaryMonth)
    );
  }

  selectSummaryMonth(month: number): void {
    if (this.isSummaryMonthDisabled(month)) {
      return;
    }
    this.summaryMonthMenuOpen = false;
    this.applySummaryMonthSelection(month, this.summaryBrowseYear);
  }

  @HostListener('document:click')
  onDocumentClickCloseMonthMenu(): void {
    if (this.summaryMonthMenuOpen) {
      this.summaryMonthMenuOpen = false;
    }
  }

  private applySummaryMonthSelection(month: number, year: number): void {
    this.dashboardState.setSummaryPeriod(month, year);
    this.reloadDashboardData();
  }

  private buildTrackHistoryPayload() {
    const period =
      this.selectedSummaryPeriod === 'all'
        ? { month: null, year: null }
        : { month: Number(this.selectedSummaryPeriod), year: this.summaryMonthYear };
    return {
      ...this.hrHttp.toTrackHistoryFilterPayload(this.locationFilters, period),
      duration: this.selectedTrackDuration,
      status: this.toTrackHistoryStatus(),
    };
  }

  private toTrackHistoryStatus(): string {
    if (this.activeTab === 'REQUESTED') return 'REQUESTED';
    if (this.activeTab === 'PENDING') return 'PENDING';
    if (this.activeTab === 'APPROVED') return 'APPROVED';
    if (this.activeTab === 'REJECTED') return 'REJECTED';
    return 'ALL';
  }

  private reloadTrackHistory(): void {
    this.trackHistorySub?.unsubscribe();
    this.beginLoading();
    this.trackHistorySub = this.getTrackHistoryRequest()
      .pipe(
        catchError((err) => {
          console.error('MRF track history failed', err);
          this.tableData = [];
          this.rowData = [];
          this.updateDisplayedRows();
          return of(null);
        }),
        finalize(() => this.endLoading())
      )
      .subscribe((history) => {
        if (history != null) {
          this.applyTrackHistoryResponse(history);
        }
      });
  }

  private getTrackHistoryRequest() {
    return this.hrHttp.getMrfTrackHistoryWithFilters(this.buildTrackHistoryPayload());
  }

  private getSummaryCountsRequest() {
    if (this.selectedSummaryPeriod === 'all') {
      return this.hrHttp.getMrfDashboard(this.locationFilters);
    }
    return this.hrHttp.getMrfDashboardDateWise(
      Number(this.selectedSummaryPeriod),
      this.summaryMonthYear,
      this.locationFilters
    );
  }

  private syncActiveTabWithPermissions(): void {
    if (!this.hasDashboardAccess) {
      this.updateDisplayedRows();
      return;
    }
    const visible = this.visibleTabs;
    if (!visible.includes(this.activeTab)) {
      this.dashboardState.setActiveTab(visible[0] ?? 'ALL');
      return;
    }
    this.updateDisplayedRows();
  }

  setTab(tab: MrfFilterTab): void {
    if (!canAccessMrfDashboardTab(tab, this.mrfPermissions)) {
      return;
    }
    if (!this.canAccessTrackHistoryTab(tab)) {
      this.hrAlert.show('No permission to access this.', 'Warning');
      return;
    }
    this.closeViewMenu();
    const changed = this.activeTab !== tab;
    this.dashboardState.setActiveTab(tab);
    if (changed) {
      this.reloadTrackHistory();
    }
  }

  onTrackDurationChange(duration: '1MONTH' | '2MONTH' | '3MONTH'): void {
    if (this.selectedTrackDuration === duration) {
      return;
    }
    this.selectedTrackDuration = duration;
    this.reloadTrackHistory();
  }

  onStatusCardClick(statusName: string): void {
    const tab = this.getFilterKeyFromStatus(statusName);
    if (!canAccessMrfDashboardTab(tab, this.mrfPermissions)) {
      return;
    }
    if (!this.canAccessTrackHistoryTab(tab)) {
      this.hrAlert.show('No permission to access this.', 'Warning');
      return;
    }
    this.closeViewMenu();
    const changed = this.activeTab !== tab;
    this.dashboardState.setActiveTab(tab);
    if (changed) {
      this.reloadTrackHistory();
    }
  }

  canAccessStatusCard(statusName: string): boolean {
    const tab = this.getFilterKeyFromStatus(statusName);
    return (
      canAccessMrfDashboardTab(tab, this.mrfPermissions) &&
      this.canAccessTrackHistoryTab(tab)
    );
  }

  private canAccessTrackHistoryTab(tab: MrfFilterTab): boolean {
    if (tab !== 'REQUESTED' && tab !== 'PENDING') {
      return true;
    }
    if (!this.trackHistoryStatusFilterActive) {
      return true;
    }
    if (tab === 'REQUESTED') {
      return this.trackHistoryAllowedStatuses.includes('REQUESTED');
    }
    return this.trackHistoryAllowedStatuses.some(
      (status) => status === 'PENDING_AT_MIS' || status === 'PENDING_AT_HR'
    );
  }

  isStatusCardActive(statusName: string): boolean {
    return this.activeTab === this.getFilterKeyFromStatus(statusName);
  }

  private updateDisplayedRows(): void {
    if (!this.hasDashboardAccess) {
      this.displayRowData = [];
      return;
    }
    if (this.activeTab === 'ALL') {
      const allowedTabStatuses = getAllowedMrfDashboardRowTabStatuses(this.mrfPermissions);
      this.displayRowData = this.rowData.filter((row) => allowedTabStatuses.has(row.tabStatus));
      return;
    }
    this.displayRowData = this.rowData.filter((row) => row.tabStatus === this.activeTab);
  }

  private getFilterKeyFromStatus(statusName: string): MrfFilterTab {
    const status = String(statusName ?? '').toUpperCase();
    if (status === 'TOTAL RECEIVED' || status === 'ALL') return 'ALL';
    if (status === 'APPROVED') return 'APPROVED';
    if (status.includes('REJECT')) return 'REJECTED';
    if (status === 'REQUESTED' || status.includes('REQUESTED')) return 'REQUESTED';
    if (status.includes('PEND') || status === 'APPROVAL' || status.includes('APPROVAL')) {
      return 'PENDING';
    }
    return 'ALL';
  }

  private applyTrackHistoryResponse(res: unknown): void {
    this.applyTrackHistoryStatusFilter(res);
    const rows = this.extractArray(res).map((item: any) => this.mapTrackHistoryRow(item));
    this.tableData = rows.sort((a, b) => {
      const orderDiff =
        this.getTableRowSortIndex(a.workflowStatus) - this.getTableRowSortIndex(b.workflowStatus);
      if (orderDiff !== 0) {
        return orderDiff;
      }
      return String(b.date ?? '').localeCompare(String(a.date ?? ''));
    });
    this.rowData = this.tableData;
    this.syncActiveTabWithTrackHistoryStatuses();
    this.updateDisplayedRows();
  }

  private applyTrackHistoryStatusFilter(res: unknown): void {
    const statuses = this.extractTrackHistoryStatuses(res);
    this.trackHistoryStatusFilterActive = statuses.length > 0;
    this.trackHistoryAllowedStatuses = statuses;
  }

  private clearTrackHistoryStatusFilter(): void {
    this.trackHistoryStatusFilterActive = false;
    this.trackHistoryAllowedStatuses = [];
  }

  private extractTrackHistoryStatuses(payload: unknown): string[] {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return [];
    }
    const list = (payload as Record<string, unknown>)['statuses'];
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((item) =>
        String(item ?? '')
          .trim()
          .toUpperCase()
          .replace(/[\s-]+/g, '_')
      )
      .filter(Boolean);
  }

  private syncActiveTabWithTrackHistoryStatuses(): void {
    if (this.canAccessTrackHistoryTab(this.activeTab)) {
      return;
    }
    this.dashboardState.setActiveTab('ALL');
  }

  private getTableRowSortIndex(workflowStatus: string): number {
    const normalized = normalizeMrfWorkflowStatus(workflowStatus);
    const index = this.tableRowSortOrder.indexOf(normalized as (typeof this.tableRowSortOrder)[number]);
    return index === -1 ? this.tableRowSortOrder.length : index;
  }

  private applyCountDetailsResponse(res: unknown): void {
    const objectMapped = this.extractDashboardCounts(res);
    const rows = this.extractArray(res).filter((item: any) => !this.isDashboardCountObject(item));
    const normalized = rows.map((item: any) => ({
      statusName: String(item?.statusName ?? item?.mrfFormStatus ?? '').toUpperCase(),
      count: Number(item?.count ?? item?.totalCount ?? 0),
      trackPercentage: Number(item?.trackPercentage ?? item?.percentage ?? 0),
    }));

    const source = objectMapped.length ? objectMapped : normalized;
    this.mrfData = this.desiredOrder.map(
      (status) =>
        source.find((item: any) => String(item.statusName).toUpperCase() === status) ?? {
          statusName: status,
          count: 0,
          trackPercentage: 0,
        }
    );
  }

  private handleSummaryCountsError(err: unknown): void {
    console.error('MRF dashboard summary failed', err);
    this.applyEmptyCountDetails();
    this.showSummaryError(this.getSummaryErrorMessage(err));
  }

  private getSummaryErrorMessage(err: unknown): string {
    const status = (err as { status?: number })?.status;
    if (status === 403) {
      return 'Summary data could not be loaded. Access denied (403).';
    }
    if (status === 401) {
      return 'Summary data could not be loaded. Session expired (401).';
    }
    if (status === 404) {
      return 'Summary data could not be loaded. API not found (404).';
    }
    const message = (err as { error?: { message?: string }; message?: string })?.error?.message
      ?? (err as { message?: string })?.message;
    if (message) {
      return `Summary data could not be loaded. ${message}`;
    }
    return 'Summary data could not be loaded. Please try again.';
  }

  private showSummaryError(message: string): void {
    this.dashboardAlert = { message, status: 'Error' };
    if (this.dashboardAlertTimer) {
      clearTimeout(this.dashboardAlertTimer);
    }
    this.dashboardAlertTimer = setTimeout(() => this.dismissDashboardAlert(), 6000);
  }

  private applyEmptyCountDetails(): void {
    this.mrfData = this.desiredOrder.map((status) => ({
      statusName: status,
      count: 0,
      trackPercentage: 0,
    }));
  }

  private mapTrackHistoryRow(item: any): any {
    const rawStatusOriginal = String(
      item?.status ??
        item?.mrfFormStatus ??
        item?.formStatus ??
        item?.mrfStatus ??
        item?.statusName ??
        'PENDING'
    ).trim();
    const rawStatus = rawStatusOriginal.toUpperCase();
    const tabStatus = this.mapToTabStatus(rawStatus);
    const workflowStatus = normalizeMrfWorkflowStatus(rawStatusOriginal || rawStatus);
    const displayStatus = this.getStatusLabel(rawStatusOriginal || rawStatus);

    const dateVal = item?.date ?? item?.createdDate ?? item?.requestedDate ?? item?.requestDate ?? '11/12/2025';
    const requestIdVal = item?.requestId ?? item?.mrfFormId ?? item?.id ?? '';

    return {
      date: this.formatDate(dateVal),
      requestId: String(requestIdVal),
      campusName: String(item?.campusName ?? item?.campus ?? '-'),
      deanName: String(item?.deanName ?? item?.principalDeanName ?? '-'),
      department: String(item?.department ?? item?.departmentName ?? '-'),
      designation: String(item?.designation ?? item?.designationName ?? '-'),
      subject: String(item?.subjectName ?? item?.subject ?? item?.subjectCode ?? '-').trim(),
      mobileNo: String(item?.mobileNo ?? item?.mobileNumber ?? '****000'),
      rawStatus: rawStatusOriginal || rawStatus,
      workflowStatus,
      tabStatus,
      status: displayStatus,
      stateId: item?.stateId ?? null,
      zoneName: item?.zoneName ?? null,
      cityId: item?.cityId ?? null,
      campusId: item?.campusId ?? item?.CampusId ?? null,
      buildingId: item?.buildingId ?? null,
    };
  }

  private mapToTabStatus(rawStatus: string): 'REQUESTED' | 'APPROVED' | 'PENDING' | 'REJECTED' {
    const workflow = normalizeMrfWorkflowStatus(rawStatus);
    if (workflow === 'APPROVED' || workflow === 'APPROVED_AT_HR') return 'APPROVED';
    if (workflow === 'REJECTED') return 'REJECTED';
    if (workflow === 'REQUESTED') return 'REQUESTED';
    if (
      workflow === 'PENDING_AT_MIS' ||
      workflow === 'PENDING_AT_HR' ||
      workflow === 'APPROVAL'
    ) {
      return 'PENDING';
    }
    return 'REQUESTED';
  }

  private formatDate(value: any): string {
    return formatMrfDateForDisplay(value, '-');
  }

  private extractArray(payload: any): any[] {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.response)) return payload.response;
    if (Array.isArray(payload?.content)) return payload.content;
    return [];
  }

  /** e.g. { totalReceived, resolved, inProgress, rejected, requested } */
  private isDashboardCountObject(value: any): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    return (
      'totalReceived' in value ||
      'requested' in value ||
      'resolved' in value ||
      'inProgress' in value ||
      'rejected' in value
    );
  }

  private extractDashboardCounts(payload: any): any[] {
    if (!payload) return [];

    const candidates: any[] = [payload];
    for (const key of ['data', 'result', 'response'] as const) {
      const nested = payload?.[key];
      if (nested != null) candidates.push(nested);
    }

    for (const candidate of candidates) {
      if (this.isDashboardCountObject(candidate)) {
        return this.mapDashboardObject(candidate);
      }
      if (Array.isArray(candidate)) {
        const countRow = candidate.find((item) => this.isDashboardCountObject(item));
        if (countRow) return this.mapDashboardObject(countRow);
      }
    }

    return [];
  }

  private mapDashboardObject(data: any): any[] {
    if (!this.isDashboardCountObject(data)) return [];

    const totalReceived = Number(data.totalReceived ?? 0);
    const requested = Number(data.requested ?? 0);
    const approved = Number(data.resolved ?? data.approved ?? 0);
    const pending = Number(data.inProgress ?? data.pending ?? 0);
    const rejected = Number(data.rejected ?? 0);
    const totalForPercent =
      totalReceived > 0 ? totalReceived : requested + approved + pending + rejected;

    const pct = (value: number) =>
      totalForPercent > 0 ? Math.round((value / totalForPercent) * 100) : 0;

    return [
      { statusName: 'TOTAL RECEIVED', count: totalReceived, trackPercentage: 100 },
      { statusName: 'REQUESTED', count: requested, trackPercentage: pct(requested) },
      { statusName: 'PENDING', count: pending, trackPercentage: pct(pending) },
      { statusName: 'APPROVED', count: approved, trackPercentage: pct(approved) },
      { statusName: 'REJECTED', count: rejected, trackPercentage: pct(rejected) },
    ];
  }

  getStatusLabel(status: string): string {
    return String(status ?? '')
      .split(/[\s_]+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  isPendingStatus(status: string): boolean {
    return status.toUpperCase() === 'PENDING';
  }

  buildStatusCell(rawStatus: string, displayLabel?: string): string {
    const { bg, color } = this.getStatusCellColors(rawStatus);
    const label = displayLabel ?? this.getStatusLabel(rawStatus);
    return `
      <button style="
        background-color: ${bg};
        color: ${color};
        border: none;
        height: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 2px;
        cursor: default;
        font-size: 10px;
        min-width: 100px;
        padding: 0 8px;
        font-weight: 600;
        white-space: nowrap;
      ">
        ${label}
      </button>
    `;
  }

  private getStatusCellColors(status: string): { bg: string; color: string } {
    const workflow = normalizeMrfWorkflowStatus(status);
    switch (workflow) {
      case 'APPROVED':
      case 'APPROVED_AT_HR':
        return { bg: '#EAFFF3', color: '#039334' };
      case 'REQUESTED':
        return { bg: '#E8F4FD', color: '#0564B1' };
      case 'PENDING_AT_MIS':
      case 'PENDING_AT_HR':
      case 'APPROVAL':
        return { bg: '#FFF3E7', color: '#FF7E00' };
      case 'REJECTED':
        return { bg: '#FFF4F1', color: '#E53935' };
      default:
        return { bg: '#EEEEEE', color: '#616161' };
    }
  }

  getIcon(status: string): string {
    switch (status.toUpperCase()) {
      case 'TOTAL RECEIVED':
        return 'assets/image/grievance/mrf-total-received.svg';
      case 'REQUESTED':
        return 'assets/image/grievance/openIcon.svg';
      case 'APPROVED':
        return 'assets/image/grievance/resolved.svg';
      case 'PENDING':
        return 'assets/image/grievance/inProgressIcon.svg';
      case 'REJECTED':
        return 'assets/image/grievance/mrf-rejected.svg';

        default:
        return 'assets/image/grievance/mrf-total-received.svg';
    }
  }

  getBarColor(status: string): string {
    switch (status.toUpperCase()) {
      case 'TOTAL RECEIVED':
        return '#026FD4';
      case 'REQUESTED':
        return '#0564B1';
      case 'APPROVED':
        return '#0CA766';
      case 'PENDING':
        return '#FB9D05';
      case 'REJECTED':
        return '#FF333A';
      default:
        return '#D9D9D9';
    }
  }
}
